import React from 'react';
import { createPortal } from 'react-dom';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
type DiffMode = 'save' | 'apply';

interface PromptDiffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  originalPrompt: string;
  newPrompt: string;
  isLoading?: boolean;
  /**
   * Modo de operação do modal:
   * - 'save': Confirmar salva as alterações no banco (botão "Salvar alterações")
   * - 'apply': Confirmar apenas aplica no editor (treinamento com IA)
   */
  mode?: DiffMode;
}

/**
 * Modal para visualizar diferenças no prompt
 * Suporta dois modos:
 * - save: Para revisar antes de salvar no banco
 * - apply: Para revisar sugestões da IA antes de aplicar no editor
 */
export const PromptDiffModal: React.FC<PromptDiffModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  originalPrompt,
  newPrompt,
  isLoading = false,
  mode = 'save',
}) => {
  if (!isOpen) return null;

  // Configurações baseadas no modo
  const modeConfig = {
    save: {
      icon: 'alert-circle',
      iconBg: '#fef3c7',
      iconColor: '#d97706',
      title: 'Revisar Alterações no Prompt',
      subtitle: 'Verifique as alterações antes de salvar',
      footerText: 'As alterações serão salvas imediatamente após a confirmação',
      confirmText: 'Confirmar e Salvar',
      loadingText: 'Salvando...',
    },
    apply: {
      icon: 'cpu',
      iconBg: '#ede9fe',
      iconColor: '#8b5cf6',
      title: 'Sugestões da IA',
      subtitle: 'Revise as alterações sugeridas pelo treinamento',
      footerText: 'As alterações serão aplicadas no editor (você ainda precisará salvar)',
      confirmText: 'Aplicar Alterações',
      loadingText: 'Aplicando...',
    },
  };

  const config = modeConfig[mode];
  const iconName = config.icon;

  // Calcula estatísticas de alterações
  const originalLines = originalPrompt.split('\n').length;
  const newLines = newPrompt.split('\n').length;
  const addedLines = Math.max(0, newLines - originalLines);
  const removedLines = Math.max(0, originalLines - newLines);

  // Estilos customizados para o diff viewer (tema claro para modal)
  const diffStyles = {
    variables: {
      light: {
        diffViewerBackground: '#ffffff',
        diffViewerColor: '#1e293b',
        addedBackground: '#dcfce7',
        addedColor: '#166534',
        removedBackground: '#fee2e2',
        removedColor: '#991b1b',
        wordAddedBackground: '#bbf7d0',
        wordRemovedBackground: '#fecaca',
        addedGutterBackground: '#d1fae5',
        removedGutterBackground: '#fecaca',
        gutterBackground: '#f8fafc',
        gutterBackgroundDark: '#f1f5f9',
        highlightBackground: '#fef3c7',
        highlightGutterBackground: '#fef3c7',
        codeFoldGutterBackground: '#e2e8f0',
        codeFoldBackground: '#f1f5f9',
        emptyLineBackground: '#f8fafc',
        gutterColor: '#64748b',
        addedGutterColor: '#166534',
        removedGutterColor: '#991b1b',
        codeFoldContentColor: '#64748b',
        diffViewerTitleBackground: '#f1f5f9',
        diffViewerTitleColor: '#1e293b',
        diffViewerTitleBorderColor: '#e2e8f0',
      },
    },
    line: {
      padding: '4px 8px',
      fontSize: '13px',
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    },
    gutter: {
      padding: '0 8px',
      minWidth: '40px',
    },
    contentText: {
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
      fontSize: '13px',
      lineHeight: '1.5',
    },
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
    >
      <div
        className="relative w-full max-w-6xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl overflow-hidden"
        style={{ backgroundColor: '#ffffff' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: config.iconBg }}
            >
              <Icon name={iconName as IconName} className="w-5 h-5" style={{ color: config.iconColor }} />
            </div>
            <div>
              <h2
                className="text-lg font-semibold"
                style={{ color: '#1e293b' }}
              >
                {config.title}
              </h2>
              <p className="text-sm" style={{ color: '#64748b' }}>
                {config.subtitle}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span
                className="px-2 py-1 rounded text-xs font-medium"
                style={{ backgroundColor: '#dcfce7', color: '#166534' }}
              >
                +{addedLines} linhas
              </span>
              <span
                className="px-2 py-1 rounded text-xs font-medium"
                style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}
              >
                -{removedLines} linhas
              </span>
            </div>

            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-2 rounded-lg transition-colors disabled:opacity-50"
              style={{ color: '#64748b' }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                  e.currentTarget.style.color = '#1e293b';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#64748b';
              }}
            >
              <Icon name="x" className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Diff Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-4">
            <div
              className="rounded-lg border overflow-hidden"
              style={{ borderColor: '#e2e8f0' }}
            >
              <ReactDiffViewer
                oldValue={originalPrompt}
                newValue={newPrompt}
                splitView={true}
                useDarkTheme={false}
                leftTitle="Versão Atual"
                rightTitle={mode === 'apply' ? 'Sugestão da IA' : 'Nova Versão'}
                compareMethod={DiffMethod.WORDS}
                styles={diffStyles}
                showDiffOnly={false}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-6 py-4 border-t"
          style={{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }}
        >
          <p className="text-sm" style={{ color: '#64748b' }}>
            {config.footerText}
          </p>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50"
              style={{
                color: '#1e293b',
                backgroundColor: '#ffffff',
                borderColor: '#e2e8f0',
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
              Cancelar
            </button>

            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
              style={{ backgroundColor: '#8b5cf6' }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = '#7c3aed';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#8b5cf6';
              }}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {config.loadingText}
                </>
              ) : (
                <>
                  <Icon name="check" className="w-4 h-4" />
                  {config.confirmText}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
